import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ShoppingCart, Heart, Star, Shield, Truck, RotateCcw, ChevronRight, Minus, Plus, Info } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import RecentlyViewedProducts, { saveRecentlyViewed } from '../components/RecentlyViewedProducts';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user, token } = useAuth();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImg, setSelectedImg] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [activeTab, setActiveTab] = useState('specs');
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const res = await axios.get(`${API}/products/${id}`);
      setProduct(res.data);
      saveRecentlyViewed(res.data);
    } catch {
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!user) { toast.error('Please login to add to cart'); navigate('/auth'); return; }
    setAdding(true);
    const result = await addToCart(product.id, quantity);
    if (result?.success) toast.success(`${product.name} added to cart!`);
    else toast.error(result?.error || 'Failed to add to cart');
    setAdding(false);
  };

  const handleBuyNow = async () => {
    if (!user) { navigate('/auth'); return; }
    setAdding(true);
    const result = await addToCart(product.id, quantity);
    setAdding(false);
    if (result?.success || result?.error === undefined) navigate('/checkout');
  };

  const handleWishlist = async () => {
    if (!user) { toast.error('Please login'); navigate('/auth'); return; }
    const result = await toggleWishlist(product.id);
    if (result?.error) { toast.error('Failed to update wishlist'); return; }
    toast.success(result.action === 'added' ? 'Added to wishlist' : 'Removed from wishlist');
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please login to review'); return; }
    if (!reviewForm.comment.trim()) { toast.error('Please write a comment'); return; }
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, { product_id: product.id, ...reviewForm }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review submitted!');
      setReviewForm({ rating: 5, comment: '' });
      fetchProduct();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const savings = product?.compare_price ? Math.round(((product.compare_price - product.price) / product.compare_price) * 100) : 0;
  const wishlisted = product ? isWishlisted(product.id) : false;

  if (loading) {
    return (
      <div className="bg-background min-h-screen">
        <Header />
        <div className="pt-[105px] container mx-auto px-4 max-w-7xl py-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="aspect-square shimmer-bg" />
            <div className="space-y-4">
              {[1,2,3,4,5].map(i => <div key={i} className="h-8 shimmer-bg" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="bg-background min-h-screen">
      <Header />
      <div className="pt-[105px]">
        {/* Breadcrumb */}
        <div className="bg-surface border-b border-white/5 py-3">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              <ChevronRight size={10} />
              <Link to="/products" className="hover:text-primary transition-colors">Products</Link>
              <ChevronRight size={10} />
              <Link to={`/products?category=${encodeURIComponent(product.category)}`} className="hover:text-primary transition-colors">{product.category}</Link>
              <ChevronRight size={10} />
              <span className="text-white/60 truncate max-w-32">{product.name}</span>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Images */}
            <div>
              <div className="relative overflow-hidden bg-surface border border-white/5 mb-3" style={{ aspectRatio: '1/1' }}>
                <img
                  src={product.images?.[selectedImg] || product.images?.[0]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                />
                {savings > 0 && (
                  <div className="absolute top-4 left-4">
                    <span className="tag-orange text-sm px-3 py-1">SAVE {savings}%</span>
                  </div>
                )}
                {product.featured && (
                  <div className="absolute top-4 right-4">
                    <span className="tag-yellow">HOT DEAL</span>
                  </div>
                )}
              </div>
              {product.images?.length > 1 && (
                <div className="flex gap-2">
                  {product.images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedImg(i)}
                      className={`w-16 h-16 overflow-hidden border-2 transition-all ${selectedImg === i ? 'border-primary' : 'border-white/10 opacity-60 hover:opacity-100'}`}
                      data-testid={`thumbnail-${i}`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product info */}
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-primary mb-1" data-testid="product-brand">{product.brand}</p>
              <h1 className="font-heading font-black text-2xl md:text-3xl uppercase text-white leading-tight mb-2" data-testid="product-title">
                {product.name}
              </h1>
              <p className="text-xs text-muted-foreground mb-1 font-mono">SKU: {product.sku}</p>

              {/* Rating */}
              {product.reviews_count > 0 && (
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex">{[1,2,3,4,5].map(s => <Star key={s} size={14} className={s <= Math.round(product.avg_rating) ? 'text-primary fill-primary' : 'text-white/20'} />)}</div>
                  <span className="text-sm text-white font-bold">{product.avg_rating}</span>
                  <span className="text-xs text-muted-foreground">({product.reviews_count} reviews)</span>
                </div>
              )}

              {/* Price section */}
              <div className="bg-surface border border-white/5 p-4 mb-4">
                <div className="flex items-end gap-3 mb-2">
                  <div className="price-tag text-3xl font-black text-primary" data-testid="product-price">AED {product.price.toLocaleString()}</div>
                  {product.compare_price && (
                    <div className="text-muted-foreground line-through text-base">AED {product.compare_price.toLocaleString()}</div>
                  )}
                  {savings > 0 && <span className="tag-orange">{savings}% OFF</span>}
                </div>

                {/* UAE Market Price Comparison */}
                {product.uae_market_price && (
                  <div className="bg-green-500/5 border border-green-500/20 p-3 mt-2">
                    <div className="flex items-center gap-2 mb-1">
                      <Info size={12} className="text-green-400" />
                      <span className="text-xs font-bold uppercase tracking-widest text-green-400">UAE Market Price Comparison</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs text-white/50">Our Price</div>
                        <div className="text-primary font-mono font-bold">AED {product.price}</div>
                      </div>
                      <div>
                        <div className="text-xs text-white/50">UAE Market</div>
                        <div className="text-white/70 font-mono">AED {product.uae_market_price}</div>
                      </div>
                      <div>
                        <div className="text-xs text-white/50">You Save</div>
                        <div className="text-green-400 font-mono font-bold">AED {(product.uae_market_price - product.price).toFixed(0)}</div>
                      </div>
                    </div>
                    <p className="text-white/30 text-xs mt-2">*Prices based on latest UAE market references. May vary.</p>
                  </div>
                )}

                {/* Stock */}
                <div className="flex items-center gap-2 mt-3">
                  {product.stock > 0 ? (
                    <>
                      <span className="w-2 h-2 bg-green-400" />
                      <span className="text-sm text-green-400 font-semibold">In Stock</span>
                      {product.stock <= 10 && <span className="text-xs text-secondary">Only {product.stock} left</span>}
                    </>
                  ) : (
                    <><span className="w-2 h-2 bg-red-500" /><span className="text-sm text-red-400">Out of Stock</span></>
                  )}
                </div>
              </div>

              {/* Quantity + Cart */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Quantity</span>
                  <div className="flex items-center border border-white/10">
                    <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-10 h-10 flex items-center justify-center text-white hover:text-primary hover:bg-white/5 transition-colors" data-testid="quantity-minus">
                      <Minus size={14} />
                    </button>
                    <span className="w-12 text-center text-white font-mono font-bold" data-testid="quantity-display">{quantity}</span>
                    <button onClick={() => setQuantity(q => Math.min(product.stock, q + 1))} className="w-10 h-10 flex items-center justify-center text-white hover:text-primary hover:bg-white/5 transition-colors" data-testid="quantity-plus">
                      <Plus size={14} />
                    </button>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleAddToCart}
                    disabled={adding || product.stock === 0}
                    className="flex-1 flex items-center justify-center gap-2 bg-primary text-black font-bold uppercase tracking-wider py-3 hover:bg-white transition-colors text-sm disabled:opacity-50"
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingCart size={16} />
                    {adding ? 'Adding...' : 'Add to Cart'}
                  </button>
                  <button
                    onClick={handleBuyNow}
                    disabled={adding || product.stock === 0}
                    className="flex-1 border border-primary/50 text-primary hover:bg-primary hover:text-black font-bold uppercase tracking-wider py-3 transition-all text-sm disabled:opacity-50"
                    data-testid="buy-now-btn"
                  >
                    Buy Now
                  </button>
                  <button onClick={handleWishlist} className={`w-12 border flex items-center justify-center transition-all ${wishlisted ? 'border-primary bg-primary/10 text-primary' : 'border-white/10 text-white/60 hover:border-primary hover:text-primary'}`} data-testid="wishlist-product-btn">
                    <Heart size={16} fill={wishlisted ? 'currentColor' : 'none'} />
                  </button>
                </div>
              </div>

              {/* Delivery & warranty */}
              <div className="grid grid-cols-3 gap-2 border-t border-white/10 pt-4">
                <div className="text-center">
                  <Truck size={18} className="text-primary mx-auto mb-1" />
                  <p className="text-xs text-white/60">UAE Delivery<br />1-3 Days</p>
                </div>
                <div className="text-center">
                  <Shield size={18} className="text-primary mx-auto mb-1" />
                  <p className="text-xs text-white/60">Warranty<br />{product.warranty || '1 Year'}</p>
                </div>
                <div className="text-center">
                  <RotateCcw size={18} className="text-primary mx-auto mb-1" />
                  <p className="text-xs text-white/60">Easy Returns<br />7 Days</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs section */}
          <div className="mt-10 border border-white/5">
            <div className="flex border-b border-white/10 overflow-x-auto">
              {['specs', 'features', 'reviews', 'faq'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-colors ${activeTab === tab ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-muted-foreground hover:text-white'}`}
                  data-testid={`tab-${tab}`}
                >
                  {tab === 'specs' ? 'Specifications' : tab === 'faq' ? 'FAQ' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            <div className="p-6">
              {activeTab === 'specs' && (
                <div>
                  <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">Technical Specifications</h3>
                  {product.specs && Object.keys(product.specs).length > 0 ? (
                    <table className="w-full text-sm" data-testid="specs-table">
                      <tbody>
                        {Object.entries(product.specs).map(([k, v], i) => (
                          <tr key={k} className={i % 2 === 0 ? 'bg-surface' : ''}>
                            <td className="py-2 px-4 text-muted-foreground font-medium w-1/3 border-b border-white/5">{k}</td>
                            <td className="py-2 px-4 text-white border-b border-white/5 font-mono text-xs">{v}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <p className="text-muted-foreground">No specifications available.</p>}
                </div>
              )}

              {activeTab === 'features' && (
                <div>
                  <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">Key Features</h3>
                  <p className="text-white/70 text-sm leading-relaxed mb-6">{product.description}</p>
                  {product.features?.length > 0 && (
                    <ul className="space-y-2">
                      {product.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-white/70">
                          <span className="w-5 h-5 bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-primary text-xs font-black">✓</span>
                          </span>
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {activeTab === 'reviews' && (
                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="text-center">
                      <div className="font-heading font-black text-5xl text-primary">{product.avg_rating || '—'}</div>
                      <div className="flex justify-center my-1">
                        {[1,2,3,4,5].map(s => <Star key={s} size={12} className={s <= Math.round(product.avg_rating || 0) ? 'text-primary fill-primary' : 'text-white/20'} />)}
                      </div>
                      <div className="text-xs text-muted-foreground">{product.reviews_count} reviews</div>
                    </div>
                  </div>

                  {/* Reviews list */}
                  {product.reviews?.length > 0 ? (
                    <div className="space-y-4 mb-6" data-testid="reviews-list">
                      {product.reviews.map(r => (
                        <div key={r.id} className="border border-white/5 p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-7 h-7 bg-primary/20 flex items-center justify-center font-bold text-primary text-sm">
                              {r.user_name?.[0]}
                            </div>
                            <div>
                              <p className="text-sm font-bold text-white">{r.user_name}</p>
                              <div className="flex">{[1,2,3,4,5].map(s => <Star key={s} size={10} className={s <= r.rating ? 'text-primary fill-primary' : 'text-white/20'} />)}</div>
                            </div>
                            <span className="ml-auto text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                          </div>
                          <p className="text-sm text-white/70">{r.comment}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm mb-6">No reviews yet. Be the first to review!</p>
                  )}

                  {/* Review form */}
                  {user && (
                    <div className="border-t border-white/10 pt-6">
                      <h4 className="font-heading font-bold text-base uppercase text-white mb-4">Write a Review</h4>
                      <form onSubmit={submitReview} className="space-y-4">
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Rating</label>
                          <div className="flex gap-2">
                            {[1,2,3,4,5].map(s => (
                              <button key={s} type="button" onClick={() => setReviewForm(f => ({ ...f, rating: s }))}
                                className={`w-8 h-8 border flex items-center justify-center transition-all ${s <= reviewForm.rating ? 'bg-primary border-primary text-black' : 'border-white/20 text-white/40 hover:border-primary/40'}`}>
                                <Star size={14} fill={s <= reviewForm.rating ? 'currentColor' : 'none'} />
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Your Review</label>
                          <textarea value={reviewForm.comment} onChange={e => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                            rows={3} placeholder="Share your experience with this product..."
                            className="w-full bg-surface border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-3 outline-none text-sm resize-none transition-colors"
                            style={{ borderRadius: 0 }} data-testid="review-comment-input" />
                        </div>
                        <button type="submit" disabled={submittingReview}
                          className="bg-primary text-black font-bold uppercase tracking-wider px-6 py-2 text-sm hover:bg-white transition-colors disabled:opacity-60"
                          data-testid="submit-review-btn">
                          {submittingReview ? 'Submitting...' : 'Submit Review'}
                        </button>
                      </form>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'faq' && (
                <div className="space-y-4">
                  <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">Frequently Asked Questions</h3>
                  {[
                    { q: 'Is this product covered under UAE warranty?', a: `Yes, the ${product.name} comes with ${product.warranty || '1 year'} manufacturer warranty valid in UAE.` },
                    { q: 'Do you offer cash on delivery in UAE?', a: 'Yes, we offer Cash on Delivery across all UAE emirates. You can also pay by card or bank transfer.' },
                    { q: 'How long does delivery take?', a: 'Standard delivery is 1-3 working days within UAE. Same-day delivery available in Dubai for orders before 2PM.' },
                    { q: 'Can I return the product if it is defective?', a: 'Yes, we accept returns within 7 days for defective items. Contact our support team for assistance.' },
                  ].map(({ q, a }, i) => (
                    <div key={i} className="border border-white/5 p-4">
                      <p className="text-sm font-bold text-primary mb-2">Q: {q}</p>
                      <p className="text-sm text-white/70">A: {a}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Related products */}
          {product.related_products?.length > 0 && (
            <div className="mt-12">
              <div className="flex items-center gap-2 mb-6">
                <div className="h-px w-6 bg-primary" />
                <h2 className="section-heading text-2xl text-white">Related Products</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {product.related_products.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            </div>
          )}

          {/* Recently Viewed */}
          <RecentlyViewedProducts excludeId={product.id} />
        </div>
      </div>
      <Footer />
    </div>
  );
}
